import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ConsentDocuments } from '@/components/consent/ConsentDocuments';
import { useApiError } from '@/hooks/useApiError';
import { useAuth } from '@/hooks/useAuth';
import { consentService } from '@/services/consentService';

const preventClose = (event: Event) => event.preventDefault();

/**
 * Blocks the app until the terms are accepted, for users the onboarding wizard no longer holds.
 * Kept separate from the wizard so an already-onboarded user is never sent back through it, and
 * so a future version bump can reuse this screen without touching onboarding.
 */
export const ConsentGate = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const { handleApiError } = useApiError();
  const { t: tCommon } = useTranslation('common');
  const [saving, setSaving] = useState(false);

  // Onboarding asks for terms as its first step, so an unfinished wizard never reaches this
  // condition. `!== true` keeps the gate shut on an absent value.
  if (user?.isProfileComplete !== true || user.hasPendingConsent !== true) {
    return null;
  }

  // The only way out. This gate paints over the navbar, so the profile page's Log out is
  // unreachable — and a failed document fetch disables Continue, which would otherwise trap
  // the user in their own account.
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      handleApiError(error, { defaultMessage: tCommon('toast.error.generic') });
    }
  };

  const handleAccept = async () => {
    setSaving(true);
    try {
      await consentService.acceptOnboardingConsent();
      // The server owns the gate, so re-read it rather than assuming the write cleared it.
      await refreshUser();
    } catch (error) {
      handleApiError(error, { defaultMessage: tCommon('toast.error.generic') });
    } finally {
      setSaving(false);
    }
  };

  return (
    // Radix owns focus containment and the body scroll lock. A bare fixed div carried
    // `aria-modal` without either, so Tab reached the app the gate is supposed to block.
    <DialogPrimitive.Root open modal>
      <DialogPrimitive.Portal>
        {/* Below the toast layer so a failed submit is still visible over the gate. */}
        <DialogPrimitive.Overlay className='fixed inset-0 z-[90] bg-black/85' />

        {/* Bounded and flex so a long document scrolls inside the reader instead of off the screen. */}
        <DialogPrimitive.Content
          aria-describedby={undefined}
          // Accepting or logging out are the only ways out — Escape and click-outside must not close it.
          onEscapeKeyDown={preventClose}
          onPointerDownOutside={preventClose}
          onInteractOutside={preventClose}
          className='fixed left-1/2 top-1/2 z-[90] flex max-h-[90vh] w-[calc(100%-2rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-gray-700 bg-[#0f0f17] p-6 shadow-2xl'
        >
          {/* Names the dialog for screen readers; the visible copy repeats it inside ConsentDocuments. */}
          <DialogPrimitive.Title className='sr-only'>
            {tCommon('consent.intro')}
          </DialogPrimitive.Title>

          <ConsentDocuments
            saving={saving}
            alreadyAccepted={false}
            onAccept={() => void handleAccept()}
            showChangeSummary
          />

          <Button
            type='button'
            variant='ghost'
            disabled={saving}
            onClick={() => void handleLogout()}
            className='mt-3 self-center text-gray-400 hover:bg-transparent hover:text-white'
          >
            <LogOut className='me-1 h-4 w-4' />
            {tCommon('buttons.logout')}
          </Button>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
